import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  ModalFooter,
  VStack,
  HStack,
  Text,
  Badge,
  useDisclosure,
  Flex,
  ButtonGroup,
  Wrap,
  WrapItem,
  useColorModeValue,
  useBreakpointValue,
  Icon,
  Divider,
  Tooltip
} from '@chakra-ui/react';
import { 
  FunnelSimple, 
  X, 
  CheckCircle, 
  SlidersHorizontal
} from '@phosphor-icons/react';

export default function MobileMediaFilters({
  allFeatures,
  selectedTags,
  filters,
  onTagClick,
  onClearFilters,
  onFiltersChange,
  formatFeatureLabel,
  isVisible = true,
  isIntegrated = false
}) {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [localFilters, setLocalFilters] = useState(filters || { features: selectedTags || [], featureFilterLogic: 'AND' });
  
  // Theme colors
  const bgColor = useColorModeValue('#F0E6D2', '#2C231B');
  const surfaceBg = useColorModeValue('#E6DAC0', '#38291E');
  const accentColor = useColorModeValue('#C19434', '#D6AD60');
  const textColor = useColorModeValue('#3B2E1E', '#E6D7B8');
  const mutedTextColor = useColorModeValue('#6B573B', '#B7A58B');
  const borderColor = useColorModeValue('#9C826B', '#6A4E33');
  
  // Responsive values with safety fallbacks
  const isMobile = useBreakpointValue({ base: true, md: false }) || false;
  const iconSize = useBreakpointValue({ base: 18, md: 20 }) || 18; // Smaller icons
  
  if (!isVisible) return null;
  
  // Only show on mobile if not integrated
  if (!isMobile && !isIntegrated) return null;
  
  // Sync localFilters with parent filters when they change or when the modal opens
  useEffect(() => {
    if (filters) {
      setLocalFilters(filters);
    } else if (selectedTags && selectedTags.length > 0) {
      setLocalFilters({ features: [...selectedTags], featureFilterLogic: filters?.featureFilterLogic || 'AND' });
    } else {
      setLocalFilters({ features: [], featureFilterLogic: filters?.featureFilterLogic || 'AND' });
    }
  }, [filters, selectedTags, isOpen]);

  const handleApplyFilters = () => {
    // First, clear all existing filters to start fresh
    onClearFilters?.();
    
    // Then apply the new filters
    if (localFilters.features && localFilters.features.length > 0) {
      // Update parent filters if available
      if (onFiltersChange) {
        onFiltersChange(localFilters);
      }
      
      // Apply each tag individually to ensure they're all selected
      localFilters.features.forEach(tagId => {
        onTagClick?.(tagId);
      });
    }
    
    // Close the modal
    onClose();
  };

  const handleLocalTagClick = (tagId) => {
    // Ensure we have an array to work with
    const currentFeatures = localFilters.features || [];
    
    const newTags = currentFeatures.includes(tagId)
      ? currentFeatures.filter(t => t !== tagId)
      : [...currentFeatures, tagId];
    
    setLocalFilters(prev => ({
      ...prev,
      features: newTags
    }));
  };

  const handleLocalClearFilters = () => {
    // Only clear the local filters, not the parent filters
    setLocalFilters({ features: [], featureFilterLogic: 'AND' });
  };

  return (
    <>
      {/* Mobile Filter Button - Always visible and prominent */}
      <Box
        position="relative"
        width="100%"
        display="flex"
        justifyContent="center"
        mt={4}
        mb={2}
        px={2}
      >
        <Button
          leftIcon={<SlidersHorizontal size={18} weight="bold" />}
          onClick={onOpen}
          bg={accentColor}
          color="white"
          size="md"
          width="100%"
          maxWidth="400px"
          borderRadius="lg"
          boxShadow="md"
          _hover={{
            bg: useColorModeValue('#A67C42', '#E6C680'),
            transform: 'translateY(-2px)',
            boxShadow: 'md'
          }}
          _active={{
            transform: 'translateY(0)'
          }}
          transition="all 0.2s ease"
          aria-label="Open filters"
          px={4}
          py={2}
        >
          Filter Features
          {selectedTags.length > 0 && (
            <Badge
              ml={2}
              bg="white"
              color={accentColor}
              borderRadius="full"
              fontSize="xs"
              fontWeight="bold"
            >
              {selectedTags.length}
            </Badge>
          )}
        </Button>
      </Box>

      {/* Mobile Filter Modal - Popup Style */}
      <Modal 
        isOpen={isOpen} 
        onClose={onClose}
        size="full"
        motionPreset="slideInBottom"
        isCentered={false}
      >
        <ModalOverlay bg="blackAlpha.600" backdropFilter="blur(3px)" />
        <ModalContent
          bg={surfaceBg}
          borderTopRadius="xl"
          position="absolute"
          bottom="0"
          mb="0"
          maxH="85vh"
          overflow="hidden"
        >
          <ModalHeader
            bg={accentColor}
            color="white"
            borderTopRadius="xl"
            py={3}
            px={4}
          >
            <Flex align="center" justify="space-between">
              <HStack spacing={2}>
                <Icon as={FunnelSimple} boxSize={5} weight="duotone" />
                <Text fontSize="md" fontWeight="bold" fontFamily="heading">
                  Feature Filters
                </Text>
              </HStack>
            </Flex>
          </ModalHeader>
          <ModalCloseButton color="white" />

          <ModalBody p={4} overflow="auto" maxH="60vh">
            <VStack spacing={4} align="stretch" width="100%">
              {/* Filter Logic */}
              {localFilters.features?.length > 1 && (
                <Box>
                  <Text
                    fontSize="md"
                    fontWeight="semibold"
                    color={textColor}
                    mb={2}
                    fontFamily="heading"
                  >
                    Filter Logic
                  </Text>
                  <ButtonGroup isAttached variant="outline" size="sm" w="full">
                    <Button
                      flex={1}
                      onClick={() => {
                        setLocalFilters(prev => ({ ...prev, featureFilterLogic: 'AND' }));
                        if (onFiltersChange) {
                          onFiltersChange({ ...localFilters, featureFilterLogic: 'AND' });
                        }
                      }}
                      bg={localFilters.featureFilterLogic !== 'OR' ? accentColor : 'transparent'}
                      color={localFilters.featureFilterLogic !== 'OR' ? 'white' : textColor}
                      borderColor={borderColor}
                      fontWeight="semibold"
                      _hover={{
                        bg: localFilters.featureFilterLogic !== 'OR' ? accentColor : surfaceBg
                      }}
                    >
                      AND (All)
                    </Button>
                    <Button
                      flex={1}
                      onClick={() => {
                        setLocalFilters(prev => ({ ...prev, featureFilterLogic: 'OR' }));
                        if (onFiltersChange) {
                          onFiltersChange({ ...localFilters, featureFilterLogic: 'OR' });
                        }
                      }}
                      bg={localFilters.featureFilterLogic === 'OR' ? accentColor : 'transparent'}
                      color={localFilters.featureFilterLogic === 'OR' ? 'white' : textColor}
                      borderColor={borderColor}
                      fontWeight="semibold"
                      _hover={{
                        bg: localFilters.featureFilterLogic === 'OR' ? accentColor : surfaceBg
                      }}
                    >
                      OR (Any)
                    </Button>
                  </ButtonGroup>
                  <Text fontSize="sm" color={mutedTextColor} mt={2} textAlign="center">
                    {localFilters.featureFilterLogic === 'OR' 
                      ? 'Show media with ANY of the selected features'
                      : 'Show media with ALL of the selected features'
                    }
                  </Text>
                </Box>
              )}

              {/* Active Filters */}
              {localFilters.features?.length > 0 && (
                <Box>
                  <Flex align="center" justify="space-between" mb={2}>
                    <Text
                      fontSize="md"
                      fontWeight="semibold"
                      color={textColor}
                      fontFamily="heading"
                    >
                      Active Filters ({localFilters.features.length})
                    </Text>
                    <Button
                      size="xs"
                      variant="ghost"
                      leftIcon={<X size={14} />}
                      onClick={handleLocalClearFilters}
                      color={mutedTextColor}
                    >
                      Clear
                    </Button>
                  </Flex>
                  <Wrap spacing={2}>
                    {localFilters.features.map(tagId => {
                      const feature = allFeatures.find(f => f.id === tagId);
                      return (
                        <WrapItem key={tagId}>
                          <Badge
                            bg={accentColor}
                            color="white"
                            borderRadius="full"
                            px={3}
                            py={2}
                            fontSize="sm"
                            fontWeight="medium"
                            cursor="pointer"
                            onClick={() => handleLocalTagClick(tagId)}
                            _active={{ transform: 'scale(0.95)' }}
                            minH="36px"
                            display="flex"
                            alignItems="center"
                          >
                            {formatFeatureLabel(feature?.label || tagId)}
                            <Icon as={X} ml={2} boxSize={3} />
                          </Badge>
                        </WrapItem>
                      );
                    })}
                  </Wrap>
                </Box>
              )}

              <Divider borderColor={borderColor} />

              {/* Available Features */}
              <Box>
                <Text
                  fontSize="md"
                  fontWeight="semibold"
                  color={textColor}
                  mb={2}
                  fontFamily="heading"
                >
                  Available Features
                </Text>
                <Wrap spacing={2}>
                  {allFeatures.map((feature) => {
                    // Check if feature is selected in local filters OR parent selected tags
                    const isSelected = localFilters.features?.includes(feature.id) || selectedTags.includes(feature.id);
                    return (
                      <WrapItem key={feature.id}>
                        <Tooltip
                          label={`${feature.count} ${feature.count === 1 ? 'item' : 'items'}`}
                          hasArrow
                        >
                          <Badge
                            bg={isSelected ? accentColor : 'transparent'}
                            color={isSelected ? 'white' : textColor}
                            borderWidth="1px"
                            borderColor={isSelected ? accentColor : borderColor}
                            borderRadius="full"
                            px={3}
                            py={2}
                            fontSize="sm"
                            fontWeight="medium"
                            cursor="pointer"
                            onClick={() => handleLocalTagClick(feature.id)}
                            _hover={{
                              bg: isSelected ? accentColor : surfaceBg,
                              borderColor: accentColor
                            }}
                            _active={{ transform: 'scale(0.95)' }}
                            minH="36px"
                            display="flex"
                            alignItems="center"
                            transition="all 0.2s ease"
                          >
                            <HStack spacing={2}>
                              <Text>{formatFeatureLabel(feature.label)}</Text>
                              <Badge
                                bg={isSelected ? 'whiteAlpha.300' : accentColor}
                                color={isSelected ? 'white' : 'white'}
                                borderRadius="full"
                                fontSize="xs"
                                minW="20px"
                                h="20px"
                                display="flex"
                                alignItems="center"
                                justifyContent="center"
                              >
                                {feature.count}
                              </Badge>
                            </HStack>
                          </Badge>
                        </Tooltip>
                      </WrapItem>
                    );
                  })}
                </Wrap>
              </Box>

              {/* Spacer for bottom padding */}
              <Box pt={3}></Box>
            </VStack>
          </ModalBody>
          
          <ModalFooter 
            p={3} 
            borderTop="1px solid" 
            borderColor={borderColor}
            bg={surfaceBg}
          >
            <Button
              variant="outline"
              size="md"
              mr={3}
              onClick={onClose}
              borderColor={borderColor}
              color={textColor}
              _hover={{
                bg: useColorModeValue('#D9CAA7', '#483123')
              }}
            >
              Cancel
            </Button>
            <Button
              size="md"
              bg={accentColor}
              color="white"
              onClick={handleApplyFilters}
              _hover={{
                bg: useColorModeValue('#A67C42', '#E6C680')
              }}
            >
              Apply Filters
              {localFilters.features?.length > 0 && (
                <Badge ml={2} bg="whiteAlpha.300" color="white">
                  {localFilters.features.length}
                </Badge>
              )}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
}